import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { Modal, Button, Form, Row, Col, Card, Spinner, InputGroup } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { BASE_URL } from "../../Config";
import "../../Pages/Company/Company.css";
import ResetPasswordModal from "./ResetPasswordModal";

const Company_Agent = ({
  companyUserId: companyUserIdProp,
  companyName: companyNameProp,
  allowRegister = true,
}) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [userId, setUserId] = useState(companyUserIdProp || null);
  const [companyName, setCompanyName] = useState(companyNameProp || "");

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    address: "",
    cnic: "",
    contactNumber: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => {
    if (companyUserIdProp) {
      setUserId(companyUserIdProp);
      setCompanyName(companyNameProp || "");
      return;
    }

    const storedUserData = sessionStorage.getItem("user");
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      if (userData.userRole === "Manager") {
        setUserId(userData.selectedCompanyId || userData.companyId || "");
        setCompanyName(userData.selectedCompanyName || userData.companyName || "");
      } else {
        setUserId(userData.userId);
        setCompanyName(userData.companyName || userData.name || "");
      }
    } else {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
    }
  }, [companyUserIdProp, companyNameProp]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!userId) {
        setAgents([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}Company_Agents?userId=${userId}`, {
          headers: { accept: "*/*" },
        });
        if (response.data.responseCode === "0000") {
          setAgents(response.data.agents || []);
        } else {
          setAgents([]);
        }
      } catch (error) {
        console.error("Error fetching agents", error);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, [userId]);

  const filteredAgents = agents.filter((agent) => {
    const isStatusMatch = statusFilter === "All" || agent.status === statusFilter;
    const isSearchMatch = agent.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return isStatusMatch && isSearchMatch;
  });

  const handleStatusSwitch = async (agentId, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "In-Active" : "Active";
    try {
      setAgents(
        agents.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: newStatus } : agent
        )
      );
      await axios.post(
        `${BASE_URL}Change_Status`,
        { agentId, status: newStatus },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );
    } catch (error) {
      console.error("Error changing agent status", error);
      setAgents(
        agents.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: currentStatus } : agent
        )
      );
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegisterAgent = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      Swal.fire("Error", "Please complete all required fields.", "error");
      return;
    }

    setFormLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}Register_Agent`,
        { userId, ...formData },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );

      Swal.fire({
        title: response.data.responseCode === "0000" ? "Success" : "Error",
        text: response.data.message,
        icon: response.data.responseCode === "0000" ? "success" : "error",
      });

      if (response.data.responseCode === "0000") {
        setFormData({
          name: "",
          email: "",
          password: "",
          address: "",
          cnic: "",
          contactNumber: "",
        });
        setShowModal(false);
        const refreshed = await axios.get(`${BASE_URL}Company_Agents?userId=${userId}`);
        if (refreshed.data.responseCode === "0000") setAgents(refreshed.data.agents);
      }
    } catch (error) {
      console.error("Register Agent Error:", error);
      Swal.fire("Error", "An unexpected error occurred. Please try again.", "error");
    } finally {
      setFormLoading(false);
    }
  };

  const openResetPassword = (row) => {
    // Only allow reset for agents currently loaded for this company
    const allowed = agents.some((a) => a.agentId === row.agentId);
    if (!allowed) {
      Swal.fire("Error", "You can only reset passwords for your company agents.", "error");
      return;
    }
    const email = row.email || row.userEmail || row.Email;
    if (!email) {
      Swal.fire(
        "Error",
        "Email is not available for this agent. Password cannot be reset.",
        "error"
      );
      return;
    }
    setResetTarget({ email, name: row.name });
  };

  const columns = [
    {
      name: "Agent Name",
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || row.userEmail || row.Email || "-",
      sortable: true,
      wrap: true,
      hide: "md",
    },
    {
      name: "CNIC",
      selector: (row) => row.cnic,
      sortable: true,
    },
    {
      name: "Contact No",
      selector: (row) => row.contactNo,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span className={`badge ${row.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`}>
          {row.status}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            title="Reset password"
            onClick={() => openResetPassword(row)}
          >
            <i className="bi bi-key" />
          </Button>
          <label className="switch mb-0">
            <input
              type="checkbox"
              checked={row.status === "Active"}
              onChange={() => handleStatusSwitch(row.agentId, row.status)}
            />
            <span className="slider round" />
          </label>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "120px",
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2 mb-0">Loading agents...</p>
      </div>
    );
  }

  return (
    <Card className="co-card">
      <Card.Header>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-person-vcard me-2 text-primary" />
              Company Agents
            </h5>
            {companyName ? (
              <small className="text-muted">Company: {companyName}</small>
            ) : null}
          </div>
          {allowRegister && (
            <Button variant="success" size="sm" onClick={() => setShowModal(true)}>
              <i className="bi bi-person-plus me-1" />
              Add Agent
            </Button>
          )}
        </div>

        <Row className="g-2 align-items-center">
          <Col xs={12} md={7}>
            <div className="d-flex flex-wrap gap-2">
              {["Active", "In-Active", "All"].map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? "primary" : "outline-secondary"}
                  onClick={() => setStatusFilter(status)}
                >
                  {status}
                </Button>
              ))}
            </div>
          </Col>
          <Col xs={12} md={5}>
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by agent name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        <div className="table-responsive">
          <DataTable
            columns={columns}
            data={filteredAgents}
            pagination
            highlightOnHover
            responsive
            striped
            noDataComponent={
              <div className="py-4 text-muted">
                <i className="bi bi-inbox fs-3 d-block mb-2" />
                No agents found
              </div>
            }
          />
        </div>
      </Card.Body>

      {allowRegister && (
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-plus me-2" />
            Register New Agent
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRegisterAgent}>
          <Modal.Body>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Floating>
                  <Form.Control
                    id="agent-name"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                  />
                  <label htmlFor="agent-name">Name *</label>
                </Form.Floating>
              </Col>
              <Col xs={12} md={6}>
                <Form.Floating>
                  <Form.Control
                    id="agent-email"
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                  />
                  <label htmlFor="agent-email">Email *</label>
                </Form.Floating>
              </Col>
              <Col xs={12}>
                <InputGroup>
                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      id="agent-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleFormChange}
                      required
                    />
                    <label htmlFor="agent-password">Password *</label>
                  </Form.Floating>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
                  </Button>
                </InputGroup>
              </Col>
              <Col xs={12}>
                <Form.Floating>
                  <Form.Control
                    id="agent-address"
                    name="address"
                    placeholder="Address"
                    value={formData.address}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="agent-address">Address</label>
                </Form.Floating>
              </Col>
              <Col xs={12} md={6}>
                <Form.Floating>
                  <Form.Control
                    id="agent-cnic"
                    name="cnic"
                    placeholder="CNIC"
                    value={formData.cnic}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="agent-cnic">CNIC</label>
                </Form.Floating>
              </Col>
              <Col xs={12} md={6}>
                <Form.Floating>
                  <Form.Control
                    id="agent-contact"
                    name="contactNumber"
                    placeholder="Contact"
                    value={formData.contactNumber}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="agent-contact">Contact Number</label>
                </Form.Floating>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={formLoading}>
              {formLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Registering...
                </>
              ) : (
                <>
                  <i className="bi bi-check2 me-1" />
                  Register Agent
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      )}

      <ResetPasswordModal
        show={!!resetTarget}
        onHide={() => setResetTarget(null)}
        email={resetTarget?.email}
        displayName={resetTarget?.name}
        roleLabel="agent"
      />
    </Card>
  );
};

export default Company_Agent;
